import { useMemo } from 'react';

import type { ActionLog } from '@/lib/types';

const MINUTES_PER_OPTIMIZATION = 2;

/**
 * The optimization impact numbers (moved from /optimizations#actions and then
 * folded into the Recent Optimizations card, rulings 09-08). All five derive
 * from the same window of action logs:
 * - searchTermsAnalysed: distinct keyword texts across keyword/negative
 *   actions (deduped, case-insensitive) — the terms optimizations acted on.
 * - optimizationsMade: the action count.
 * - accuracy: the engine's own average confidence_level across actions that
 *   carry a matched rule (self-reported confidence, not measured accuracy).
 * - timeSaved: actions × 2 minutes, in hours — an estimate by definition.
 * - budgetReallocated: summed historical spend of the keywords the actions
 *   touched — spend now steered elsewhere, not money literally moved.
 */
export function computeOptimizationImpact(actionLogs: ActionLog[]) {
  const optimizationsMade = actionLogs.length;

  const searchTerms = new Set<string>();
  actionLogs.forEach((action) => {
    if (action.entity_type === 'keyword' || action.entity_type === 'negative_keyword') {
      const term =
        (action.after_state?.keyword_text as string) ||
        (action.after_state?.keyword as string) ||
        (action.after_state?.negative_keyword as string) ||
        (action.details?.keyword_text as string);
      if (term) searchTerms.add(term.toLowerCase());
    }
  });

  let totalConfidence = 0;
  let confidenceCount = 0;
  actionLogs.forEach((action) => {
    const confidence = action.after_state?.matched_rules?.[0]?.confidence_level;
    if (confidence != null) {
      totalConfidence += confidence;
      confidenceCount++;
    }
  });
  const accuracy = confidenceCount > 0 ? (totalConfidence / confidenceCount) * 100 : 0;

  const timeSaved = (optimizationsMade * MINUTES_PER_OPTIMIZATION) / 60;

  let budgetReallocated = 0;
  actionLogs.forEach((action) => {
    const cost = action.after_state?.matched_rules?.[0]?.performance_metrics?.cost;
    if (cost != null) budgetReallocated += cost;
  });

  return { searchTermsAnalysed: searchTerms.size, optimizationsMade, accuracy, timeSaved, budgetReallocated };
}

/** Compact stat strip for the Recent Optimizations card header area. */
export function OptimizationImpactStats({
  currencySymbol,
  actionLogs,
}: {
  currencySymbol: string;
  actionLogs: ActionLog[];
}) {
  const metrics = useMemo(() => computeOptimizationImpact(actionLogs), [actionLogs]);

  // A window with zero actions is measured-empty: the sums are true zeros
  // (nothing touched, nothing steered) and render as such — only the
  // confidence ratio, whose denominator is the actions themselves, dashes.
  // With actions present, a missing cost/confidence field stays a dash
  // (unknown, not zero).
  const emptyWindow = actionLogs.length === 0;

  const stats: { label: string; value: string; tooltip: string }[] = [
    {
      label: 'Keywords touched',
      value: metrics.searchTermsAnalysed.toLocaleString(),
      tooltip: 'Distinct keywords the optimizations acted on: breadth, not raw action volume',
    },
    {
      label: 'Optimizations',
      value: String(metrics.optimizationsMade),
      tooltip: 'Optimization actions in this period',
    },
    {
      label: 'Confidence',
      value: metrics.accuracy > 0 ? `${metrics.accuracy.toFixed(1)}%` : '-',
      tooltip: "The engine's average confidence score for its matched rules",
    },
    {
      label: 'Time saved',
      value: metrics.timeSaved > 0 || emptyWindow ? `${metrics.timeSaved.toFixed(1)}h` : '-',
      tooltip: 'Estimated hours saved vs manual review (~2 min per optimization)',
    },
    {
      label: 'Budget steered',
      value: metrics.budgetReallocated > 0
        ? `${currencySymbol}${metrics.budgetReallocated.toFixed(2)}`
        : emptyWindow ? `${currencySymbol}0.00` : '-',
      tooltip: 'Historical spend of the keywords these optimizations touched',
    },
  ];

  return (
    // Five equal columns, always: five stats in an auto-fit grid wrapped to
    // 4 + 1, and an orphaned tile reads as broken (ruling 09-09). Labels
    // truncate at narrow widget widths with the title carrying the full text.
    <div className="mb-3 grid grid-cols-5 gap-2">
      {stats.map((stat) => (
        <div key={stat.label} className="min-w-0 rounded-md bg-gray-50 px-2.5 py-1.5" title={stat.tooltip}>
          <p className="truncate text-[10px] uppercase tracking-wider text-gray-500">{stat.label}</p>
          <p className="text-sm font-semibold text-gray-900">{stat.value}</p>
        </div>
      ))}
    </div>
  );
}
